import { actionCreator, type ExtractPayload } from '@ardoq/rxbeach';
import type { PermissionContextUser } from '@ardoq/access-control';
import type {
  DashboardApiError,
  DashboardBuilderWidget,
  EditModeDashboardDataState,
  LoadedDashboard,
  SelectedBranch,
} from '@ardoq/dashboard';
import type { Layout, LoadingState } from '../types';
import type {
  APIChartWidgetAttributes,
  APIDashboardFilterExpression,
  APIDashboardFilterParameter,
  APILinkedDashboardFilterParameter,
  APIReportFilters,
  ArdoqId,
  BarChartColorOptions,
  DashboardFilterValues,
  DataSourceField,
  HexColor,
  ReportColumn,
  WidgetDataSourceEntity,
  WidgetGlobalFilterBindings,
  WidgetTypes,
} from '@ardoq/api-types';
import type { Maybe } from '@ardoq/common-helpers';

const ACTION_PREFIX = '[dashboardBuilder]';
const addPrefix = (actionName: string): `[${string}] ${string}` =>
  `${ACTION_PREFIX} ${actionName}`;

export type CreateWidgetPayload = {
  viewType?: WidgetTypes;
  newWidgetId: string;
  layout: Omit<Layout, 'i'>;
};

export type CopyWidgetPayload = {
  widgetId: string;
  newWidgetId: string;
  layout: Omit<Layout, 'i'>;
  shouldSetAsSelected: boolean;
};

export type CopyDashboardUrLParams = {
  id: ArdoqId;
  dashboardFilterValues?: DashboardFilterValues;
};

export const createWidget = actionCreator<CreateWidgetPayload>(
  addPrefix('CREATE_WIDGET')
);

export const selectWidget = actionCreator<string | null>(
  addPrefix('SELECT_WIDGET')
);

export const copyWidget = actionCreator<CopyWidgetPayload>(
  addPrefix('COPY_WIDGET')
);

export type UpdateWidgetPayload = Partial<DashboardBuilderWidget> & {
  _id: string;
};
export const updateWidget = actionCreator<UpdateWidgetPayload>(
  addPrefix('UPDATE_WIDGET')
);

export const setWidgets = actionCreator<DashboardBuilderWidget[]>(
  addPrefix('SET_WIDGETS')
);

export const deleteWidget = actionCreator<string>(addPrefix('DELETE_WIDGET'));

export type SelectDataSourcePayload = {
  selectedDataSourceEntity: WidgetDataSourceEntity;
  widgetId: ArdoqId;
};

export const selectDataSource = actionCreator<SelectDataSourcePayload>(
  addPrefix('SELECT_DATA_SOURCE')
);

export type SelectFieldPayload = {
  dataSourceFields: DataSourceField[];
  widgetId: string;
};

export const selectField = actionCreator<SelectFieldPayload>(
  addPrefix('SELECT_FIELD')
);

export const fetchWidgetDataSources = actionCreator(
  addPrefix('FETCH_AVAILABLE_DATA_SOURCES')
);

export const setAvailableDataSources = actionCreator<WidgetDataSourceEntity[]>(
  addPrefix('SET_WIDGET_DATA_SOURCES')
);

export const setLoadingDataSources = actionCreator<boolean>(
  addPrefix('SET_LOADING_DATA_SOURCES')
);

export type SelectWidgetTypePayload = {
  widgetType: WidgetTypes;
  widgetId: ArdoqId;
};

export const selectWidgetType = actionCreator<SelectWidgetTypePayload>(
  addPrefix('SELECT_WIDGET_TYPE')
);

export type SetWidgetsPreviewPayload = {
  previewWidgets: APIChartWidgetAttributes[];
  hasActiveDashboardFilters: boolean;
};
export const setWidgetsPreview = actionCreator<SetWidgetsPreviewPayload>(
  addPrefix('SET_WIDGETS_PREVIEW')
);

export const setWidgetsErrors = actionCreator<
  Array<{ _id: string; error: string }>
>(addPrefix('SET_WIDGETS_ERRORS'));

export const setDashboardName = actionCreator<string>(
  addPrefix('SET_DASHBOARD_NAME')
);

export const setDashboardDescription = actionCreator<string>(
  addPrefix('SET_DASHBOARD_DESCRIPTION')
);

export const saveDashboard = actionCreator<
  EditModeDashboardDataState & {
    availableDataSources: WidgetDataSourceEntity[];
    currentUser: PermissionContextUser;
    folderId: Maybe<ArdoqId>;
  }
>(addPrefix('SAVE_DASHBOARD'));

export const setLoadedDashboard = actionCreator<LoadedDashboard>(
  addPrefix('SET_LOADED_DASHBOARD')
);

export const clearDashboardData = actionCreator(
  addPrefix('CLEAR_DASHBOARD_DATA')
);

export const setDashboardFolderId = actionCreator<Maybe<ArdoqId>>(
  addPrefix('SET_DASHBOARD_FOLDER_ID')
);

export const setShowLinkChartPopover = actionCreator<boolean>(
  addPrefix('SET_SHOW_LINK_CHART_POPOVER')
);

export const setDashboardLoadingState = actionCreator<{
  apiLoadingState: LoadingState.LOADING | null;
  apiLoadingDashboardId?: ArdoqId;
  apiLoadingGlobalFilterValues?: DashboardFilterValues;
}>(addPrefix('SET_LOADING_STATE'));

export type SetColorForDataPointPayload = {
  dataPoint: string;
  color: HexColor;
};
export const setColorForDataPoint = actionCreator<SetColorForDataPointPayload>(
  addPrefix('SET_COLOR_FOR_FIELD')
);

export const removeManuallyAssignedColorForDataPoint = actionCreator<string>(
  addPrefix('REMOVE_MANUALLY_ASSIGNED_COLOR_FOR_DATAPOINT')
);

export const setShowValidationErrors = actionCreator(
  addPrefix('SET_SHOW_VALIDATION_ERRORS')
);

export const setDashboardWarnings = actionCreator(
  addPrefix('SET_DASHBOARD_WARNINGS')
);

export const persistLoadedDashboardData = actionCreator<LoadedDashboard>(
  addPrefix('PERSIST_LOADED_DASHBOARD_DATA')
);

export type FetchDashboardDataPayload = {
  dashboardId: ArdoqId;
  allowStaleWhileRevalidate: boolean;
  presentationId?: ArdoqId;
  slideId?: ArdoqId;
  branchId?: SelectedBranch;
  globalFilterValues?: DashboardFilterValues;
  stateId?: ArdoqId;
};
export const fetchDashboardData = actionCreator<FetchDashboardDataPayload>(
  addPrefix('FETCH_DASHBOARD_DATA')
);

export const fetchDashboardDataInBackground = actionCreator<
  Omit<
    FetchDashboardDataPayload,
    'presentationId' | 'slideId' | 'allowStaleWhileRevalidate'
  >
>(addPrefix('FETCH_DASHBOARD_DATA_IN_BACKGROUND'));

export type SetDashboardApiErrorPayload = {
  apiError: DashboardApiError;
  apiLoadingState?:
    | LoadingState.FETCH_DATA_SOURCES_ERROR
    | LoadingState.LOADING_ERROR
    | LoadingState.SAVING_ERROR;
};

export type DashboardWidgetFilterPayload = {
  widgetId: ArdoqId;
  filters: APIReportFilters | null;
  columns: ReportColumn[];
};

export const setDashboardApiError = actionCreator<SetDashboardApiErrorPayload>(
  addPrefix('SET_DASHBOARD_API_ERROR')
);

export type SelectBarChartColorSelection = {
  colorSelection: BarChartColorOptions;
  widgetId: ArdoqId;
};

export const selectBarChartColorSelection =
  actionCreator<SelectBarChartColorSelection>(
    addPrefix('SELECT_BAR_CHART_COLOR_SELECTION')
  );

export type SelectNumberSlicesPayload = {
  numberSlices: number;
  widgetId: ArdoqId;
};
export const selectNumberSlices = actionCreator<SelectNumberSlicesPayload>(
  addPrefix('SELECT_NUMBER_SLICES')
);

export const widgetFiltersAppliedOnDrawerSave =
  actionCreator<DashboardWidgetFilterPayload>(
    addPrefix('APPLY_WIDGET_FILTERS_ON_DRAWER_SAVE')
  );

export const widgetFilterDrawerOpened = actionCreator(
  addPrefix('WIDGET_FILTER_DRAWER_OPENED')
);

export const widgetFilterDrawerClosed = actionCreator(
  addPrefix('WIDGET_FILTER_DRAWER_CLOSED')
);

export const setIsDashboardInListMode = actionCreator<boolean>(
  addPrefix('SET_IS_DASHBOARD_IN_LIST_MODE')
);

export const createDashboardFilterParameter = actionCreator<{
  name: string;
  id: ArdoqId;
}>(addPrefix('CREATE_DASHBOARD_FILTER_PARAMETER'));

export const removeDashboardFilterParameter = actionCreator<ArdoqId>(
  addPrefix('REMOVE_DASHBOARD_FILTER_PARAMETER')
);

export const updateDashboardFilterParameterName = actionCreator<{
  parameterId: ArdoqId;
  newName: string;
}>(addPrefix('UPDATE_DASHBOARD_FILTER_PARAMETER_NAME'));

export const reorderDashboardFilterParameters = actionCreator<{
  fromIndex: number;
  toIndex: number;
}>(addPrefix('REORDER_DASHBOARD_FILTER_PARAMETERS'));

export type WidgetDashboardFiltersAppliedPayload = {
  widgetId: ArdoqId;
  dashboardFilterBindings: WidgetGlobalFilterBindings | undefined;
  linkModeDashboardFilterParameter: APIDashboardFilterParameter | undefined;
};
export const widgetDashboardFiltersAppliedOnDrawerSave =
  actionCreator<WidgetDashboardFiltersAppliedPayload>(
    addPrefix('WIDGET_DASHBOARD_FILTERS_APPLIED_ON_DRAWER_SAVE')
  );

export const updateDashboardFilterValues = actionCreator<{
  parameterId: ArdoqId;
  values: APIDashboardFilterExpression[] | null;
}>(addPrefix('UPDATE_DASHBOARD_FILTER_VALUES'));

export const clearAllDashboardFilterValuesClicked = actionCreator(
  addPrefix('CLEAR_ALL_DASHBOARD_FILTER_VALUES_CLICKED')
);

export const setLinkModeDashboardFilterParameterId =
  actionCreator<ArdoqId | null>(
    addPrefix('SET_LINK_MODE_DASHBOARD_FILTER_PARAMETER_ID')
  );

export const editWidgetLinkModeDashboardFilterBinding = actionCreator<ArdoqId>(
  addPrefix('EDIT_WIDGET_LINK_MODE_DASHBOARD_FILTER_BINDING')
);

export const unlinkWidgetWithDashboardFilter = actionCreator<{
  widgetId: ArdoqId;
  dashboardFilterParameterId: ArdoqId;
}>(addPrefix('UNLINK_WIDGET_WITH_DASHBOARD_FILTER'));

export const linkWidgetWithDashboardFilter = actionCreator<{
  widgetId: ArdoqId;
  dashboardFilterParameterId: ArdoqId;
}>(addPrefix('LINK_WIDGET_WITH_DASHBOARD_FILTER'));

export const copyDashboardUrlWithStateIdButtonWasClicked =
  actionCreator<CopyDashboardUrLParams>(
    addPrefix('COPY_DASHBOARD_URL_WAS_REQUESTED')
  );

export const dashboardFilterPillValuePopoverToggled = actionCreator<{
  dashboardFilterId: string;
  isOpen: boolean;
}>(addPrefix('DASHBOARD_FILTER_PILL_VALUE_POPOVER_TOGGLED'));

export const dashboardFilterPillOperatorPopoverToggled = actionCreator<{
  dashboardFilterId: string;
  isOpen: boolean;
}>(addPrefix('DASHBOARD_FILTER_PILL_OPERATOR_POPOVER_TOGGLED'));

export const dashboardFilterDatePickerToggled = actionCreator<{
  dashboardFilterId: string;
  isOpen: boolean;
}>(addPrefix('DASHBOARD_FILTER_PILL_DATE_PICKER_TOGGLED'));

export const dashboardFilterPillInputValueChanged = actionCreator<{
  dashboardFilterId: string;
  inputValue: string | number | null;
}>(addPrefix('DASHBOARD_FILTER_PILL_INPUT_VALUE_CHANGED'));
export const triggerAutoLinkDashboardFilterToWidgets = actionCreator<{
  dashboardFilterParameter: APILinkedDashboardFilterParameter;
  widgetIdsToLink: ArdoqId[];
  widgetIdsNeedOverride: ArdoqId[];
}>(addPrefix('TRIGGER_AUTO_LINK_DASHBOARD_FILTER_TO_WIDGETS'));

export type DashboardFilterAutoLinkPrompt = ExtractPayload<
  typeof triggerAutoLinkDashboardFilterToWidgets
>;

export const autoLinkDashboardFilterToWidgets = actionCreator<{
  dashboardFilterParameter: APILinkedDashboardFilterParameter;
  widgetIdsToLink: ArdoqId[];
}>(addPrefix('AUTO_LINK_DASHBOARD_FILTER_TO_WIDGETS'));

export const autoLinkPromptAccepted =
  actionCreator<DashboardFilterAutoLinkPrompt>(
    addPrefix('AUTO_LINK_PROMPT_ACCEPTED')
  );

export const autoLinkPromptDismissed = actionCreator(
  addPrefix('AUTO_LINK_PROMPT_DISMISSED')
);

export type SetPendingConditionSelectionPayload = {
  widgetId: string;
  columnKey: string;
  conflictType: 'chartFilter' | 'dashboardFilter';
};

export const setPendingConditionSelection =
  actionCreator<SetPendingConditionSelectionPayload>(
    addPrefix('SET_PENDING_CONDITION_SELECTION')
  );

export const clearPendingConditionSelection = actionCreator(
  addPrefix('CLEAR_PENDING_CONDITION_SELECTION')
);

export const confirmPendingConditionSelection = actionCreator(
  addPrefix('CONFIRM_PENDING_CONDITION_SELECTION')
);
